import React from "react";
import { ArrowRight, CheckCircle2, Compass, Workflow, Linkedin } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { mock } from "@/mock";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Reveal } from "@/components/site/SiteLayout";
import { getLinkedInAvatarUrl } from "@/api/linkedinService";

function Glass({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-shadow duration-200 hover:shadow-[0_0_26px_rgba(0,194,255,0.3)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Core team members list - avatars are fetched dynamically via their LinkedIn profile URL.
 * When a member updates their photo on LinkedIn, the site automatically reflects it.
 */
const CORE_TEAM_MEMBERS = [
  {
    name: "Uday Pratap Singh",
    role: "Full Stack Developer",
    department: "Engineering & Systems",
    bio: "Full Stack Developer at Shiveda IT Services, specializing in scalable web systems, modern React/Node architectures, and AI application engineering.",
    linkedin: "https://www.linkedin.com/in/udaysingh902",
    initials: "US",
  },
  {
    name: "Ayush Yadav",
    role: "Full Stack Developer",
    department: "Engineering & Systems",
    bio: "Full Stack Developer at Shiveda IT Services, specializing in full-stack web applications, distributed systems, and modern AI engineering workflows.",
    linkedin: "https://www.linkedin.com/in/ayush-yadav-8baa5a261/",
    initials: "AY",
  },
  {
    name: "Arpit Mani Tripathi",
    role: "Full Stack Developer",
    department: "Engineering & Systems",
    bio: "Full Stack Developer at Shiveda IT Services, specializing in scalable web applications, cloud architectures, and modern software engineering.",
    linkedin: "https://www.linkedin.com/in/arpitmanitripathi",
    initials: "AT",
  },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="w-full px-5 py-16 xl:px-10 2xl:px-14">
      <Reveal delay={0}>
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-black/40 px-4 py-2 text-xs text-white/70">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          About
        </div>
      </Reveal>

      <Reveal delay={90}>
        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-white md:text-4xl">
          A development-focused partner for teams that need reliable shipping
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
          Shiveda IT Services is built around one belief: premium user experience
          only matters when the engineering is clean, fast, and maintainable.
          We blend UI/UX, scalable architecture, and disciplined delivery.
        </p>
      </Reveal>

      <Reveal delay={160}>
        <div className="mt-7 flex flex-wrap gap-2">
          {["Product engineering", "Design systems", "ERP + SaaS", "Cloud-ready"].map(
            (t) => (
              <Badge
                key={t}
                variant="secondary"
                className="rounded-full border-white/10 bg-white/[0.05] text-white/80 hover:bg-white/[0.05]"
              >
                {t}
              </Badge>
            ),
          )}
        </div>
      </Reveal>

      <Separator className="my-12 bg-white/10" />

      {/* Founder Story Section */}
      <Reveal delay={100}>
        <Glass className="p-8">
          <div className="text-xs tracking-[0.22em] text-white/55 text-center">
            FOUNDER
          </div>
          <div className="mt-3 text-2xl font-semibold tracking-tight text-white text-center">
            Meet the Visionary Behind Shiveda
          </div>
          
          <div className="mt-6 space-y-8">
            {/* Founder Image - Centered */}
            <div className="flex justify-center">
              <div className="relative group" style={{perspective: '1000px'}}>
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-transparent blur-xl transition-all duration-700 ease-out group-hover:blur-2xl group-hover:from-primary/30" />
                <img
                  src="/Founder.png"
                  alt="Shivangi Pandey - Founder of Shiveda IT Services"
                  className="relative h-[240px] w-[240px] rounded-full border border-white/10 object-cover shadow-[0_18px_50px_rgba(0,0,0,0.4)] group-hover:shadow-[0_25px_60px_rgba(0,194,255,0.4)]"
                  style={{
                    transition: 'transform 0.1s ease-out, box-shadow 0.3s ease-out',
                    transformStyle: 'preserve-3d',
                    willChange: 'transform'
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    const centerX = rect.width / 2;
                    const centerY = rect.height / 2;
                    const rotateX = ((y - centerY) / centerY) * 15;
                    const rotateY = ((x - centerX) / centerX) * -15;
                    e.currentTarget.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
                  }}
                />
                {/* LinkedIn Icon positioned at bottom right of image */}
                <a
                  href="https://www.linkedin.com/in/shivangi-pandey-0345ba3a6/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/80 backdrop-blur-sm transition-all duration-300 hover:border-primary/50 hover:bg-primary/20 hover:scale-110"
                  aria-label="Shivangi Pandey LinkedIn Profile"
                >
                  <Linkedin className="h-5 w-5 text-white hover:text-primary transition-colors" />
                </a>
              </div>
            </div>
            
            {/* Founder Story Text - Below Image */}
            <div className="space-y-4 text-sm leading-relaxed text-white/70 text-center">
              <p>
                Shivangi Pandey didn't wait for an opportunity to come her way. Instead, she built one from the ground up.
              </p>
              <p>
                Growing up in a small town, Shivangi always had a natural instinct for leadership and a drive to create something of her own. She noticed that while the world was moving fast into the digital age, her own region lacked a dedicated home for high quality digital building. Rather than looking elsewhere for a solution, she decided to become the solution herself.
              </p>
              <p>
                As a self taught entrepreneur, Shivangi built the Shiveda brand through sheer determination and a vision for what a modern IT firm should look like. Today, she leads the company with the same homegrown heart that started it all. She is a woman who wears many hats, successfully navigating the responsibilities of being a wife and a mother while excelling as a business leader.
              </p>
              <p>
                For Shivangi, Shiveda is more than just a company. It is an empowerment story that proves you can build a brand right from where you are. She continues to lead by example, showing that with enough grit and a clear vision, a modern woman can manage a household and a boardroom with equal excellence.
              </p>
            </div>
          </div>
        </Glass>
      </Reveal>

      <Separator className="my-12 bg-white/10" />

      {/* Core Engineering Team / Meet the Builders Section */}
      <Reveal delay={110}>
        <Glass className="p-8">
          <div className="text-xs tracking-[0.22em] text-white/55 text-center">
            CORE TEAM
          </div>
          <div className="mt-3 text-2xl font-semibold tracking-tight text-white text-center">
            Meet the Builders & Engineers
          </div>
          <p className="mt-2 text-center text-sm text-white/70 max-w-xl mx-auto">
            Architecting modern digital products, scalable cloud infrastructures, and production AI engineering systems.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CORE_TEAM_MEMBERS.map((member) => (
              <div
                key={member.linkedin}
                className="rounded-2xl border border-white/10 bg-black/40 p-6 flex flex-col justify-between hover:border-primary/40 transition-colors"
              >
                <div>
                  <div className="flex items-center gap-4">
                    <div className="relative h-16 w-16 rounded-2xl overflow-hidden border border-white/15 bg-white/5 flex-shrink-0 shadow-lg">
                      <img
                        src={getLinkedInAvatarUrl(member.linkedin)}
                        alt={member.name}
                        className="w-full h-full object-cover object-center"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = e.target.nextSibling;
                          if (fallback) fallback.style.display = "flex";
                        }}
                      />
                      <div className="w-full h-full hidden items-center justify-center bg-gradient-to-br from-primary/25 to-primary/5 text-primary font-bold text-lg">
                        {member.initials}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white tracking-tight">
                        {member.name}
                      </h3>
                      <p className="text-xs text-primary font-medium mt-0.5">
                        {member.role}
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs leading-relaxed text-white/70">
                    {member.bio}
                  </p>
                </div>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs text-white/50">{member.department}</span>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs font-semibold text-white/80 hover:text-primary transition-colors"
                    aria-label={`${member.name} LinkedIn Profile`}
                  >
                    <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                    <span>LinkedIn Profile</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </Glass>
      </Reveal>

      <Separator className="my-12 bg-white/10" />

      <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
        <Reveal delay={120}>
          <Glass className="p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-xs tracking-[0.22em] text-white/55">
                  STORY
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  Built for long-term partnerships
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">
                  Many teams get stuck choosing between speed and quality.
                  We focus on repeatable delivery: clear scope, reusable UI,
                  clean APIs, and performance that holds up under real traffic.
                </p>
              </div>
              <div className="grid h-11 w-20 place-items-center rounded-full border border-white/10 bg-black/55">
                <Compass className="h-5 w-5 text-primary" />
              </div>
            </div>

            <div className="mt-7 grid gap-3">
              {[{
                t: "Vision",
                d: "Deliver premium digital products that stay maintainable at scale.",
              }, {
                t: "Mission",
                d: "Help teams ship reliable software with clarity and confidence.",
              }, {
                t: "Promise",
                d: "No hype—just measurable outcomes and transparent execution.",
              }].map((i) => (
                <div
                  key={i.t}
                  className="rounded-2xl border border-white/10 bg-black/40 p-4"
                >
                  <div className="text-sm font-medium text-white">{i.t}</div>
                  <div className="mt-1 text-sm text-white/70">{i.d}</div>
                </div>
              ))}
            </div>
          </Glass>
        </Reveal>

        <Reveal delay={200}>
          <Glass className="p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-xs tracking-[0.22em] text-white/55">
                  VALUES
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  How we work
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">
                  We optimize for momentum while protecting code quality.
                </p>
              </div>
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-black/55">
                <Workflow className="h-5 w-5 text-primary" />
              </div>
            </div>

            <div className="mt-7 space-y-4">
              {[{
                t: "Clarity before code",
                d: "We align on success metrics, edge cases, and delivery plan early.",
              }, {
                t: "Design systems, not one-offs",
                d: "Reusable components keep UI consistent and iteration fast.",
              }, {
                t: "Performance as a feature",
                d: "Fast load, responsive interaction, and stable UI—by default.",
              }, {
                t: "Ownership & reporting",
                d: "Weekly check-ins, transparent progress, and predictable delivery.",
              }].map((i) => (
                <div key={i.t} className="flex gap-3">
                  <div className="mt-1 grid h-6 w-6 place-items-center rounded-full border border-white/10 bg-black/55">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{i.t}</div>
                    <div className="mt-1 text-sm text-white/70">{i.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </Glass>
        </Reveal>
      </div>

      <Separator className="my-12 bg-white/10" />

      <Reveal delay={220}>
        <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
          <Glass className="p-8">
            <div className="text-xs tracking-[0.22em] text-white/55">
              PROCESS
            </div>
            <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
              A simple, reliable delivery loop
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              We keep things lightweight, documented, and measurable.
            </p>

            <div className="mt-7 grid gap-3 md:grid-cols-2">
              {["Discovery", "Design", "Build", "QA & Launch", "Monitor", "Iterate"].map(
                (p, idx) => (
                  <div
                    key={p}
                    className="rounded-2xl border border-white/10 bg-black/40 p-4"
                  >
                    <div className="text-xs text-white/55">0{idx + 1}</div>
                    <div className="mt-1 text-sm font-medium text-white">{p}</div>
                  </div>
                ),
              )}
            </div>
          </Glass>

          <Glass className="relative overflow-hidden p-8">
            <div className="pointer-events-none absolute -top-20 right-[-120px] h-[260px] w-[260px] rounded-full bg-[radial-gradient(closest-side,rgba(0,194,255,0.18),transparent_65%)]" />
            <div className="pointer-events-none absolute -bottom-28 left-[-140px] h-[320px] w-[320px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.10),transparent_65%)]" />

            <div className="relative">
              <div className="text-xs tracking-[0.22em] text-white/55">NEXT</div>
              <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                Let’s scope your build
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/70">
                Tell us what you’re building and what success looks like.
                We’ll share a scoped plan and timeline.
              </p>
              <Button
                //onClick={() => navigate("/contact#form")}
                onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                className="mt-7 h-[52px] w-full rounded-xl bg-primary text-black btn-glow hover:bg-primary/90"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Glass>
        </div>
      </Reveal>

      <Separator className="my-12 bg-white/10" />

      <Reveal delay={260}>
        <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-8 backdrop-blur-[18px]">
          <div className="text-sm font-medium text-white">Contact</div>
          <div className="mt-2 text-sm text-white/70">
            {mock.company.contact.location} • {mock.company.contact.email}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
